/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, BoxView, Text } from '@components/atoms'

import { useAppSelector, useAppDispatch, logDashboardActions } from '@redux/index'
import type { LogEntryType } from '@redux/types'

import { blue, red, green, grey } from '@mui/material/colors'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'

const getStatusColor = (code: number | undefined) => {
	if (!code) return 'default'
	if (code >= 200 && code < 300) return 'success'
	if (code >= 400 && code < 500) return 'warning'
	if (code >= 500) return 'error'
	return 'default'
}

const getTagColor = (tag: string) => {
	switch (tag) {
		case 'REQ':
			return blue['A400']
		case 'RES':
			return green[600]
		case 'ERROR':
			return red['A400']
		default:
			return grey[500]
	}
}

const LogListPage = () => {
	const dispatch = useAppDispatch()

	const listDate = useAppSelector(state => state.logDashboard.listDate)
	const listLevel = useAppSelector(state => state.logDashboard.listLevel)
	const listCountry = useAppSelector(state => state.logDashboard.listCountry)
	const listMethod = useAppSelector(state => state.logDashboard.listMethod)
	const listUrl = useAppSelector(state => state.logDashboard.listUrl)
	const listPage = useAppSelector(state => state.logDashboard.listPage)
	const listLimit = useAppSelector(state => state.logDashboard.listLimit)
	const logs = useAppSelector(state => state.logDashboard.logs)
	const logsTotalCount = useAppSelector(state => state.logDashboard.logsTotalCount)
	const logsStatus = useAppSelector(state => state.logDashboard.logsStatus)

	// trace
	const traces = useAppSelector(state => state.logDashboard.traces)
	const traceModalOpen = useAppSelector(state => state.logDashboard.traceModalOpen)
	const tracesStatus = useAppSelector(state => state.logDashboard.tracesStatus)

	// detail modal
	const detailModalOpen = useAppSelector(state => state.logDashboard.detailModalOpen)
	const detailModalLog = useAppSelector(state => state.logDashboard.detailModalLog)

	React.useEffect(() => {
		dispatch(logDashboardActions.TRIGGER_GET_LOG_LIST(true))
	}, [listDate, listPage, listLimit])

	const handleSearch = () => {
		dispatch(logDashboardActions.LIST_PAGE(1))
		dispatch(logDashboardActions.TRIGGER_GET_LOG_LIST(true))
	}

	const handleRowClick = (log: LogEntryType) => {
		dispatch(logDashboardActions.DETAIL_MODAL_LOG(log))
		dispatch(logDashboardActions.DETAIL_MODAL_OPEN(true))
	}

	const handleTraceClick = (e: React.MouseEvent, requestId: string) => {
		e.stopPropagation()
		if (!requestId) return
		dispatch(logDashboardActions.TRACE_REQUEST_ID(requestId))
		dispatch(logDashboardActions.TRIGGER_GET_TRACE(true))
	}

	const handleCloseDetailModal = () => {
		dispatch(logDashboardActions.DETAIL_MODAL_OPEN(false))
		dispatch(logDashboardActions.DETAIL_MODAL_LOG(null))
	}

	const handleCloseTraceModal = () => {
		dispatch(logDashboardActions.TRACE_MODAL_OPEN(false))
		dispatch(logDashboardActions.TRACES([]))
	}

	const handlePageChange = (_: unknown, newPage: number) => {
		dispatch(logDashboardActions.LIST_PAGE(newPage + 1))
	}

	const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		dispatch(logDashboardActions.LIST_LIMIT(parseInt(e.target.value)))
		dispatch(logDashboardActions.LIST_PAGE(1))
	}

	const isLoading = logsStatus === 'loading'

	return (
		<View css={style}>
			<BoxView title="로그 상세 목록">
				<View className="listContainer">
					{/* Filters */}
					<View className="filterRow">
						<TextField
							type="date"
							label="날짜"
							value={listDate}
							onChange={e => dispatch(logDashboardActions.LIST_DATE(e.target.value))}
							size="small"
							InputLabelProps={{ shrink: true }}
						/>
						<FormControl size="small" style={{ minWidth: 120 }}>
							<InputLabel>레벨</InputLabel>
							<Select
								value={listLevel}
								label="레벨"
								onChange={e => dispatch(logDashboardActions.LIST_LEVEL(e.target.value))}
							>
								<MenuItem value="">전체</MenuItem>
								<MenuItem value="INFO">INFO</MenuItem>
								<MenuItem value="ERROR">ERROR</MenuItem>
								<MenuItem value="DEBUG">DEBUG</MenuItem>
							</Select>
						</FormControl>
						<TextField
							label="국가"
							value={listCountry}
							onChange={e => dispatch(logDashboardActions.LIST_COUNTRY(e.target.value))}
							size="small"
							placeholder="예: KR"
						/>
						<FormControl size="small" style={{ minWidth: 120 }}>
							<InputLabel>Method</InputLabel>
							<Select
								value={listMethod}
								label="Method"
								onChange={e => dispatch(logDashboardActions.LIST_METHOD(e.target.value))}
							>
								<MenuItem value="">전체</MenuItem>
								<MenuItem value="GET">GET</MenuItem>
								<MenuItem value="POST">POST</MenuItem>
								<MenuItem value="PUT">PUT</MenuItem>
								<MenuItem value="DELETE">DELETE</MenuItem>
							</Select>
						</FormControl>
						<TextField
							label="URL 검색"
							value={listUrl}
							onChange={e => dispatch(logDashboardActions.LIST_URL(e.target.value))}
							size="small"
							placeholder="/api/..."
						/>
						<Button variant="contained" onClick={handleSearch}>
							검색
						</Button>
					</View>

					{/* Table */}
					{isLoading ? (
						<View className="loadingView">
							<CircularProgress />
						</View>
					) : (
						<>
							<TableContainer component={Paper} variant="outlined">
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>시간</TableCell>
											<TableCell>태그</TableCell>
											<TableCell>Method</TableCell>
											<TableCell>URL</TableCell>
											<TableCell>Status</TableCell>
											<TableCell>응답시간</TableCell>
											<TableCell>국가</TableCell>
											<TableCell>사용자</TableCell>
											<TableCell>Request ID</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{(logs ?? []).map((log: LogEntryType, idx: number) => (
											<TableRow
												key={`${log.requestId}-${log.tag}-${idx}`}
												hover
												sx={{ cursor: 'pointer' }}
												onClick={() => handleRowClick(log)}
											>
												<TableCell style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
													{log.timestamp}
												</TableCell>
												<TableCell>
													<Chip
														label={log.tag}
														size="small"
														sx={{
															backgroundColor: getTagColor(log.tag),
															color: '#fff',
															fontWeight: 600,
															fontSize: '0.7rem',
														}}
													/>
												</TableCell>
												<TableCell>
													<Text category="p2" style={{ fontWeight: 600 }}>
														{log.method ?? '-'}
													</Text>
												</TableCell>
												<TableCell
													style={{
														fontFamily: 'monospace',
														fontSize: '0.8rem',
														maxWidth: 250,
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
													}}
												>
													{log.url ?? '-'}
												</TableCell>
												<TableCell>
													{log.statusCode ? (
														<Chip
															label={log.statusCode}
															size="small"
															color={getStatusColor(log.statusCode) as any}
															variant="outlined"
														/>
													) : (
														'-'
													)}
												</TableCell>
												<TableCell>
													{log.responseTime != null ? `${log.responseTime}ms` : '-'}
												</TableCell>
												<TableCell>{log.country || '-'}</TableCell>
												<TableCell style={{ fontSize: '0.8rem' }}>
													{log.userId || log.userEmail || '-'}
												</TableCell>
												<TableCell>
													{log.requestId ? (
														<Chip
															label={log.requestId.substring(0, 12) + '...'}
															size="small"
															variant="outlined"
															color="primary"
															clickable
															onClick={e => handleTraceClick(e, log.requestId)}
														/>
													) : (
														'-'
													)}
												</TableCell>
											</TableRow>
										))}
										{(logs ?? []).length === 0 && (
											<TableRow>
												<TableCell colSpan={9} align="center" style={{ padding: '2rem' }}>
													데이터 없음
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>
							<TablePagination
								component="div"
								count={logsTotalCount}
								page={listPage - 1}
								onPageChange={handlePageChange}
								rowsPerPage={listLimit}
								onRowsPerPageChange={handleRowsPerPageChange}
								rowsPerPageOptions={[25, 50, 100]}
								labelRowsPerPage="페이지당 행"
							/>
						</>
					)}
				</View>
			</BoxView>

			{/* Detail Modal */}
			<Dialog
				open={detailModalOpen}
				onClose={handleCloseDetailModal}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle
					sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
				>
					로그 상세
					<IconButton onClick={handleCloseDetailModal}>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					{detailModalLog && (
						<pre
							style={{
								backgroundColor: grey[900],
								color: green['A400'],
								padding: '1rem',
								borderRadius: '8px',
								overflow: 'auto',
								fontSize: '0.8rem',
								lineHeight: 1.6,
								maxHeight: '60vh',
							}}
						>
							{JSON.stringify(detailModalLog, null, 2)}
						</pre>
					)}
				</DialogContent>
				<DialogActions>
					{detailModalLog?.requestId && (
						<Button
							onClick={e => {
								handleCloseDetailModal()
								handleTraceClick(e as any, detailModalLog.requestId)
							}}
							color="primary"
						>
							Trace 보기
						</Button>
					)}
					<Button onClick={handleCloseDetailModal}>닫기</Button>
				</DialogActions>
			</Dialog>

			{/* Trace Modal */}
			<Dialog
				open={traceModalOpen}
				onClose={handleCloseTraceModal}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle
					sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
				>
					Request Trace
					<IconButton onClick={handleCloseTraceModal}>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					{tracesStatus === 'loading' ? (
						<View style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
							<CircularProgress />
						</View>
					) : (traces ?? []).length === 0 ? (
						<Text category="p2" style={{ textAlign: 'center', padding: '2rem' }}>
							Trace 데이터 없음
						</Text>
					) : (
						<View className="traceTimeline">
							{(traces ?? []).map((trace: LogEntryType, idx: number) => (
								<View key={`${trace.tag}-${idx}`} className="traceItem">
									<View
										className="traceDot"
										style={{ backgroundColor: getTagColor(trace.tag) }}
									/>
									{idx < (traces ?? []).length - 1 && <View className="traceConnector" />}
									<Paper elevation={2} sx={{ p: 2, flex: 1 }}>
										<View
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '0.5rem',
												marginBottom: '0.5rem',
											}}
										>
											<Chip
												label={trace.tag}
												size="small"
												sx={{
													backgroundColor: getTagColor(trace.tag),
													color: '#fff',
													fontWeight: 600,
												}}
											/>
											<Text category="c2" style={{ color: grey[500] }}>
												{trace.timestamp}
											</Text>
										</View>
										{trace.tag === 'REQ' && (
											<>
												<Text category="p2" style={{ fontFamily: 'monospace' }}>
													{`${trace.method} ${trace.url}`}
												</Text>
												<Text category="c2" style={{ color: grey[600] }}>
													{`${trace.clientIP} / ${trace.country} ${trace.city}`}
												</Text>
											</>
										)}
										{trace.tag === 'RES' && (
											<Text category="p2">
												{`Status: ${trace.statusCode} | ${trace.responseTime}ms`}
											</Text>
										)}
										{trace.tag === 'ERROR' && (
											<Text category="p2" style={{ color: red['A400'] }}>
												{`${trace.error?.code}: ${trace.error?.message}`}
											</Text>
										)}
									</Paper>
								</View>
							))}
						</View>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseTraceModal}>닫기</Button>
				</DialogActions>
			</Dialog>
		</View>
	)
}

export default LogListPage

const style = css({
	'.listContainer': {
		width: '100%',
		padding: '1rem',
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
	},

	'.filterRow': {
		display: 'flex',
		alignItems: 'center',
		gap: '0.75rem',
		flexWrap: 'wrap',
	},

	'.loadingView': {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '20rem',
	},

	'.traceTimeline': {
		display: 'flex',
		flexDirection: 'column',
		gap: '0',
		padding: '1rem 0',
	},

	'.traceItem': {
		display: 'flex',
		alignItems: 'flex-start',
		gap: '1rem',
		position: 'relative',
		paddingBottom: '1rem',
	},

	'.traceDot': {
		width: '14px',
		height: '14px',
		borderRadius: '50%',
		flexShrink: 0,
		marginTop: '1rem',
		zIndex: 1,
	},

	'.traceConnector': {
		position: 'absolute',
		left: '6px',
		top: '26px',
		width: '2px',
		height: 'calc(100% - 14px)',
		backgroundColor: '#ccc',
	},
})
